import axios from 'axios';
import { getSessionToken } from './sessionTokenService';

const syncApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1`,
  timeout: 30000, // 30 second timeout for sync operations
});

// Add request interceptor for session token
syncApi.interceptors.request.use(
  (config) => {
    const sessionToken = getSessionToken();
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface SyncResponse {
  success: boolean;
  message: string;
  data: {
    ticker: string;
    status: string;
    sync_id?: string;
  };
  metadata: {
    queryDate: string;
    ticker: string;
    syncStarted: boolean;
  };
}

export interface SyncOnlyTickerBasicsResponse {
  success: boolean;
  message: string;
  data: {
    ticker_symbol: string;
    status: 'already_exists' | 'created' | 'failed';
    ticker_basics_id: number | null;
  };
}

export const syncApiService = {
  /**
   * Ensure ticker_basics record exists (create from Polygon if missing).
   * Call this before adding a new ticker to the user list when authenticated,
   * so that updateTickerMappings can succeed.
   */
  async syncOnlyTickerBasics(ticker: string): Promise<SyncOnlyTickerBasicsResponse> {
    try {
      const response = await syncApi.post('/data-processor/sync-only-ticker-basics', {
        tickerSymbol: ticker,
      });
      return response.data;
    } catch (error) {
      console.error('Sync only ticker basics API error:', error);
      throw error;
    }
  },

  async syncTicker(
    ticker: string,
    options?: { syncProsAndCons?: boolean },
  ): Promise<SyncResponse> {
    try {
      const response = await syncApi.post('/data-processor/sync', {
        tickerSymbol: ticker,
        ...(options?.syncProsAndCons ? { syncProsAndCons: true } : {}),
      });
      return response.data;
    } catch (error) {
      console.error('Sync API error:', error);
      throw error;
    }
  },
};

export default syncApiService;
